import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import moment from 'moment';
import styled from 'styled-components';
import { rgba } from 'polished';

import { ShuffleIcon, SignInIcon } from '@treams/treams-icons';
import { Button, Label, Popover } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

const Transition = styled(Box)`
	position: relative;
	background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
	width: 24px;
	height: 24px;
	padding: 4px;
	border-radius: 16px;

	svg {
		display: block;

		path {
			fill: ${(props) => props.theme.colors.primary};
		}
	}
`;

const JobPositionTitle = styled(Box)`
	font-size: 14px;
	font-weight: bold;
`;

const JobPositionLevel = styled(Box)`
	font-size: 12px;
	color: ${(props) => props.theme.colors.slateBlue};
`;

const Item = styled(Flex)<{ active: boolean; selected: boolean }>`
	height: 48px;
	border-radius: 6px;
	justify-content: center;
	flex-direction: column;
	padding: 0px 12px;
	cursor: pointer;

	${(props) =>
		props.selected
			? `
        background: ${props.theme.colors.primary};
        border: 1px solid ${props.theme.colors.primary};
        box-shadow: 0px 0px 0px 3px ${rgba(props.theme.colors.primary, 0.25)};

        ${JobPositionTitle} {
            color: white;
        }

        ${JobPositionLevel} {
            color: rgba(255, 255, 255, 0.5);
        }
    `
			: props.active
			? `
        background: #ffffff;
        border: 1px solid #c2cad2;
        box-shadow: 0px 2px 4px rgba(194, 202, 210, 0.25);
    `
			: `
        border: 1px solid #D8E0E8;
    `}
`;

const TransitionLabel = styled(Flex)`
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
	flex-direction: column;
	white-space: pre;
	top: 42px;
	margin-top: 8px;
	align-items: center;
`;

const TransitionDate = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
	line-height: 30px;
	height: 30px;
`;

const TransitionBadge = styled(Box)`
	color: ${(props) => props.theme.colors.primary};
	line-height: 30px;
	height: 30px;
	padding: 0px 6px;

	background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
	border-radius: 4px;
`;

function JobPositionPopover(props: { user: graphql.User; position: graphql.JobPosition }) {
	const { t } = useTranslation();

	const [archive] = graphql.useArchiveJobPositionMutation();
	const [update] = graphql.useUpdateJobPositionMutation();

	const startDate = props.position.startDate ? moment(props.position.startDate) : undefined;
	const endDate = props.position.endDate ? moment(props.position.endDate) : undefined;
	const isFutureJobPosition = startDate?.isAfter(moment());

	const startDateLabel = startDate
		? startDate.format('DD-MM-YYYY')
		: t('settings.employees.jobPositions.job.startDate.unknown');

	return (
		<Box p={3}>
			{isFutureJobPosition ? (
				<Label mb={3}>
					{t('settings.employees.jobPositions.job.startsOn', { startDate: startDateLabel })}
				</Label>
			) : (
				<Label mb={3}>
					{startDateLabel}
					{' – '}
					{endDate
						? endDate.format('DD-MM-YYYY')
						: t('settings.employees.jobPositions.job.endDate.unknown')}
				</Label>
			)}
			<Flex>
				{endDate || isFutureJobPosition ? (
					<Button
						type="destructive"
						title={
							isFutureJobPosition
								? t('settings.employees.jobPositions.job.cancelButton')
								: t('settings.employees.jobPositions.job.deleteButton')
						}
						onClick={async () => {
							await archive({ variables: { input: { id: props.position.id } } });
						}}
					/>
				) : (
					<Box mr={2}>
						<Button
							type="transparent"
							title={t('settings.employees.jobPositions.job.terminateButton')}
							onClick={async () => {
								await update({
									variables: {
										input: {
											id: props.position.id,
											jobTitleID: props.position.jobTitle.id,
											startDate: props.position.startDate,
											userID: props.user.id,
											endDate: new Date(),
											externalReferenceID: props.position.externalReferenceID,
											jobLevelID: props.position.jobLevel?.id,
										},
									},
								});
							}}
						/>
					</Box>
				)}
			</Flex>
		</Box>
	);
}

export default function JobPosition(props: {
	user: graphql.User;
	position: graphql.JobPosition;
	index: number;
}) {
	const { t } = useTranslation();

	const { localize } = useLocalize();
	const [selected, setSelected] = useState(false);

	const startDate = props.position.startDate ? moment(props.position.startDate) : undefined;
	const endDate = props.position.endDate ? moment(props.position.endDate) : undefined;

	return (
		<>
			<Transition mr={2} flexGrow={0} flexShrink={0}>
				{props.index === 0 ? <SignInIcon /> : <ShuffleIcon />}
				<TransitionLabel pl={props.index === 0 ? 3 : 0}>
					<TransitionDate>{moment(props.position.startDate).format('DD MMM YYYY')}</TransitionDate>
					<TransitionBadge>
						{props.index === 0
							? t('settings.employees.jobPositions.transition.joined')
							: t('settings.employees.jobPositions.transition.changed')}
					</TransitionBadge>
				</TransitionLabel>
			</Transition>
			<Box flexGrow={0} flexShrink={0}>
				<Popover.Target
					visible={selected}
					onDismiss={() => setSelected(false)}
					contents={<JobPositionPopover user={props.user} position={props.position} />}
				>
					<Item
						onClick={() => setSelected(true)}
						selected={selected}
						active={
							!props.position.isArchived &&
							(startDate?.isBefore(moment()) ?? true) &&
							(endDate?.isAfter(moment()) ?? true)
						}
						mr={2}
					>
						<JobPositionTitle>{localize(props.position.jobTitle.title).text}</JobPositionTitle>
						{props.position.jobLevel && (
							<JobPositionLevel>{localize(props.position.jobLevel.title).text}</JobPositionLevel>
						)}
					</Item>
				</Popover.Target>
			</Box>
		</>
	);
}
